/**
 * MobileChartFallback Component
 * Used in: Dashboard screen - provides fallback display for charts on mobile devices
 * Used by: IncomeVsCOGSChart, ComprehensiveTrendChart, QuarterlyBreakdown
 */
import React from 'react';
import { RotateCcw, BarChart3 } from 'lucide-react';

interface MobileChartFallbackProps {
  title: string;
  data: any[];
  dataKeys: string[];
  formatValue?: (value: number) => string;
  showRotateMessage?: boolean;
}

export const MobileChartFallback: React.FC<MobileChartFallbackProps> = ({ 
  title, 
  data, 
  dataKeys, 
  formatValue = (val) => val.toLocaleString(),
  showRotateMessage = true 
}) => {
  // Show only the last 6 data points for mobile
  const limitedData = data.slice(-6);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      
      {showRotateMessage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 rotate-message">
          <div className="flex items-center gap-2 text-blue-800">
            <RotateCcw className="w-4 h-4" />
            <p className="text-sm font-medium">
              Rotate to landscape for full interactive chart
            </p>
          </div>
          <p className="text-xs text-blue-600 mt-1">
            Showing recent data points below
          </p>
        </div>
      )}

      {/* Simplified data table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-1 font-medium text-gray-900">Period</th>
              {dataKeys.map(key => (
                <th key={key} className="text-right py-2 px-1 font-medium text-gray-900 text-xs">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {limitedData.map((item, index) => (
              <tr key={index} className="border-b border-gray-100">
                <td className="py-2 px-1 font-medium text-gray-900 text-xs">
                  {item.month || item.quarter || item.season || item.year || `Item ${index + 1}`}
                </td>
                {dataKeys.map(key => (
                  <td key={key} className="text-right py-2 px-1 text-gray-700 text-xs">
                    {item[key] !== undefined ? formatValue(item[key]) : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
